import React, { useMemo, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatLastActivity } from '../TerritoryGrid/territoryUtils'
import { fetchOnlinePresence } from '../../services/presence.api'
import './homePanel.css'
import './HomeStatsPanel.css'

const STATUS_ICONS = {
  active: '●',
  war: '⚔',
  adventure: '◈',
  character: '◇',
  time: '◷',
}

const ONLINE_POLL_MS = 45_000

function displayName(entry) {
  const name = entry.characterName || 'Sin nombre'
  const clan = entry.clanName
  if (clan && !name.toLowerCase().includes(String(clan).toLowerCase())) {
    return `${clan} ${name}`
  }
  return name
}

export default function HomeStatsPanel({ stats, loading, territories = [] }) {
  const pulse = useMemo(() => {
    const list = territories || []
    return {
      atPeace: list.filter((t) => t.territory_status === 'Paz').length,
      atWar: list.filter((t) => t.territory_status === 'Guerra').length,
    }
  }, [territories])

  const [online, setOnline] = useState([])
  const [anonymousCount, setAnonymousCount] = useState(0)
  const [onlineLoading, setOnlineLoading] = useState(true)
  const [onlineError, setOnlineError] = useState(false)

  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()

    const load = async () => {
      try {
        const data = await fetchOnlinePresence({ signal: controller.signal })
        if (cancelled) return
        setOnline(Array.isArray(data?.online) ? data.online : [])
        setAnonymousCount(Number(data?.anonymousCount || 0))
        setOnlineError(false)
      } catch {
        if (cancelled) return
        setOnlineError(true)
      } finally {
        if (!cancelled) setOnlineLoading(false)
      }
    }

    load()
    const timer = setInterval(load, ONLINE_POLL_MS)

    return () => {
      cancelled = true
      controller.abort()
      clearInterval(timer)
    }
  }, [])

  return (
    <section className="home-stats-panel home-bento-card home-bento-card--pulse" aria-label="Crónicas del Mundo">
      <header className="home-panel__header">
        <span className="home-panel__eyebrow">記 · En vivo</span>
        <h2 className="home-panel__title">Crónicas del Mundo</h2>
      </header>

      <ul className="home-stats-panel__pulse">
        <li className="home-stats-panel__pulse-item home-stats-panel__pulse-item--time">
          <span className="home-stats-panel__pulse-seal" aria-hidden="true">
            {STATUS_ICONS.time}
          </span>
          <div className="home-stats-panel__pulse-body">
            <span className="home-stats-panel__pulse-label">Última actividad</span>
            <span className="home-stats-panel__pulse-value">
              {loading ? '…' : stats?.lastActivity ? formatLastActivity(stats.lastActivity) : 'Sin registros'}
            </span>
          </div>
        </li>

        <li className="home-stats-panel__pulse-item home-stats-panel__pulse-item--active">
          <span className="home-stats-panel__pulse-seal" aria-hidden="true">
            {STATUS_ICONS.active}
          </span>
          <div className="home-stats-panel__pulse-body">
            <span className="home-stats-panel__pulse-label">En calma</span>
            <span className="home-stats-panel__pulse-value">
              {loading ? '…' : pulse.atPeace}
            </span>
            <span className="home-stats-panel__pulse-hint">territorios en paz</span>
          </div>
        </li>

        <li className="home-stats-panel__pulse-item home-stats-panel__pulse-item--war">
          <span className="home-stats-panel__pulse-seal" aria-hidden="true">
            {STATUS_ICONS.war}
          </span>
          <div className="home-stats-panel__pulse-body">
            <span className="home-stats-panel__pulse-label">En guerra</span>
            <span className="home-stats-panel__pulse-value">
              {loading ? '…' : pulse.atWar}
            </span>
            <span className="home-stats-panel__pulse-hint">territorios en conflicto</span>
          </div>
        </li>

        <li className="home-stats-panel__pulse-item home-stats-panel__pulse-item--adventures">
          <span className="home-stats-panel__pulse-seal" aria-hidden="true">
            {STATUS_ICONS.adventure}
          </span>
          <div className="home-stats-panel__pulse-body">
            <span className="home-stats-panel__pulse-label">Aventuras</span>
            <span className="home-stats-panel__pulse-value">
              {loading ? '…' : Number(stats?.adventures || 0)}
            </span>
            <span className="home-stats-panel__pulse-hint">crónicas en el mundo</span>
          </div>
        </li>

        <li className="home-stats-panel__pulse-item home-stats-panel__pulse-item--characters">
          <span className="home-stats-panel__pulse-seal" aria-hidden="true">
            {STATUS_ICONS.character}
          </span>
          <div className="home-stats-panel__pulse-body">
            <span className="home-stats-panel__pulse-label">Personajes</span>
            <span className="home-stats-panel__pulse-value">
              {loading ? '…' : Number(stats?.characters || 0)}
            </span>
            <span className="home-stats-panel__pulse-hint">shinobis en el archivo</span>
          </div>
        </li>
      </ul>

      <div className="home-stats-panel__online" aria-live="polite">
        <header className="home-stats-panel__online-header">
          <span className="home-stats-panel__online-eyebrow">旅 · Caminando por el mundo</span>
          <h3 className="home-stats-panel__online-title">Conectados</h3>
          {!onlineLoading && !onlineError && (
            <span className="home-stats-panel__online-count">
              {online.length + anonymousCount} en el tablón
            </span>
          )}
        </header>

        {onlineLoading && (
          <p className="home-stats-panel__online-empty">Consultando el tablón…</p>
        )}

        {!onlineLoading && onlineError && (
          <p className="home-stats-panel__online-empty">No se pudo cargar la presencia.</p>
        )}

        {!onlineLoading && !onlineError && online.length === 0 && anonymousCount === 0 && (
          <p className="home-stats-panel__online-empty">Nadie en el tablón ahora.</p>
        )}

        {!onlineLoading && !onlineError && (online.length > 0 || anonymousCount > 0) && (
          <p className="home-stats-panel__online-flow">
            {online.map((entry, index) => (
              <span key={`${entry.userId}-${entry.characterId}`} className="home-stats-panel__online-chip">
                {index > 0 && <span className="home-stats-panel__online-sep">, </span>}
                {entry.characterSlug ? (
                  <Link to={`/personajes/${entry.characterSlug}`} className="home-stats-panel__online-name">
                    {displayName(entry)}
                  </Link>
                ) : (
                  <span className="home-stats-panel__online-name">{displayName(entry)}</span>
                )}
              </span>
            ))}
            {anonymousCount > 0 && (
              <span className="home-stats-panel__online-anon">
                {online.length > 0 ? ', ' : ''}
                y {anonymousCount} viajero{anonymousCount === 1 ? '' : 's'} sin nombre
              </span>
            )}
          </p>
        )}      </div>
    </section>
  )
}
